/**
 * Current editor content ID
 * @return {Number}
 */
import { mandatoryFields } from '@/modules/content/config';
import isObject from 'lodash/isObject';

// Master channel
const master = state => state.original.channels.master;
const channel = state => state.channels[state.currentChannel] || {};
const draft = (state, getters) => getters.channel.draft;
const form = (state, getters) => getters.channel.form;
const formFields = (state, getters) => getters.channel.formFields;
const formErrors = (state, getters) => getters.channel.formErrors;
const editorFieldsConfig = state => (state.fieldsConfig[state.currentChannel] || state.fieldsConfig || [])
  .reduce((acc, fieldConfig) => {
    if (fieldConfig.field) {
      acc[fieldConfig.field] = fieldConfig;
    }
    return acc;
  }, {});
const hasRedirection = state => !!state.channels.master.draft.redirection;
const redirectMetadata = (state, getters) => getters.draft.redirection;
const masterLanguage = (state) => {
  const lang = state.original.channels.master.language;
  if (!lang) {
    return null;
  }
  if (isObject(lang)) {
    return lang.value || null;
  }
  return lang || null;
};
const editingTranslation = (state, getters) => {
  if (Object.keys(state.channels).length === 1) {
    return false;
  }
  return state.currentLanguage && getters.masterLanguage !== state.currentLanguage;
};
const hasScheduledPublication = (state, getters) => {
  if (state.currentChannel === 'master') {
    return getters.draft.schedulePublish != null;
  }
  return state.channels.master.draft.schedulePublish != null;
};
const mandatoryFieldsErrors = (state, getters) => getters.formErrors
  .filter(error => mandatoryFields.includes(error.field));

const requiredForPublishErrors = (state, getters) => Object
  .entries(getters.formFields)
  .filter(([field, fieldForm]) => fieldForm.validForPublish === false)
  .map(([field, fieldForm]) => field);

// Content current channels
const modifiedChannels = state => Object.keys(state.channels);
// Content available channels for customization
const availableChannels = (state) => {
  // It will be available if channel is not present con modified channels
  const modChannels = modifiedChannels(state);
  return state.availableChannels.map(item => item.channel).filter(key => !modChannels.includes(key));
};

// Channel unbounded fields
const unboundFields = state => state.original.channels[state.currentChannel].unboundFields || [];
// Channel fields to bind
const bindFields = (state, getters) => getters.draft.bindFields || [];

const currentChannelMetadata = (state) => {
  const currentChannel = state.availableChannels.find(item => item.channel === state.currentChannel);
  if (!currentChannel) {
    return {};
  }
  return currentChannel;
};

// Current channel alternative preview urls
const alternativePreviewUrls = (state, getters) => {
  if (!getters.currentChannelMetadata.alternativePreviewUrls) {
    return [];
  }
  return getters.currentChannelMetadata.alternativePreviewUrls;
};

const channelActionsConfig = (state, getters) => {
  if (!getters.currentChannelMetadata.actions) {
    return [];
  }
  return getters.currentChannelMetadata.actions;
};

const channelActionConfigByActionName = (state, getters) => (name) => {
  const actionConfig = getters.channelActionsConfig.find(config => config.action === name);
  return actionConfig || null;
};

const channelServerBlock = (state, getters) => {
  if (!getters.currentChannelMetadata.locked) {
    return null;
  }
  return getters.currentChannelMetadata.lockedMessage || 'serverBlock';
};

const channelHeaderButtonsComponent = (state, getters) => getters.currentChannelMetadata.headerButtonsComponent || null;

// Current channel preview Url
const previewUrl = (state, getters) => {
  if (state.selectedPreviewUrl) {
    return state.selectedPreviewUrl;
  }

  if (getters.currentChannelMetadata.previewUrl) {
    return getters.currentChannelMetadata.previewUrl;
  }

  if (getters.alternativePreviewUrls.length > 0) {
    return getters.alternativePreviewUrls[0].previewUrl;
  }

  return null;
};

// Current channel public Url
const publicUrl = (state, getters) => {
  if (!getters.currentChannelMetadata.publicUrl) {
    return null;
  }
  return getters.currentChannelMetadata.publicUrl;
};

const title = (state, getters) => getters.master.title;

const scheduleDate = (state, getters) => getters.master.schedulePublish;

const id = state => state.id;

const isDraft = state => state.isDraft;

const type = state => state.type;

const metadata = (state, getters) => {
  let res = state.original;

  if (getters.master.firstPublicationDate) {
    res = { ...res, firstPublicationDate: getters.master.firstPublicationDate };
  }

  return res;
};

const publicationDate = state => state.original.publishedDate;

const isPublished = state => state.versionPublished || state.entityPublished;

const versionIsPublished = state => state.versionPublished;

const creationDate = (state, getters) => getters.metadata.creationDate;

const publishOnVideoTranscoded = (state, getters) => getters.metadata.publishOnVideoTranscoded;

const isVideoTranscoded = (state, getters) => getters.draft.transcoded;

const actionsLog = (state) => {
  const log = state.original.actionsLog || { channels: [] };
  return log.channels.reduce((acc, current) => {
    acc[current.channel] = current.actions;
    return acc;
  }, {});
};

const scheduleUnpublishDate = (state, getters) => getters.master.scheduleUnpublish;

const unpublishedDate = state => state.original.unpublishedDate;

const channelActionsLog = (state, getters) => getters.actionsLog[state.currentChannel] || [];

// determina si la acción de "publicar" requiere confirmación
const confirmToPublish = state => !!state.original.confirmToPublish;

// Preload the preview in content editor
const preloadPreview = (state, getters, rootState) => {
  if ('preloadPreview' in rootState.config.content) {
    return !!rootState.config.content.preloadPreview;
  }
  return true;
};

// Editor is in version mode. Multiple drafts and versions
const versionable = state => !!state.relativeVersionId;

const hasBeenPublished = state => state.hasBeenPublished;

const entityPublishedVersionStartPublicationDate = state => state.entityPublishedVersionStartPublicationDate;

const advancedMode = state => state.advancedMode;

export default {
  actionsLog,
  alternativePreviewUrls,
  availableChannels,
  bindFields,
  channel,
  channelActionConfigByActionName,
  channelActionsConfig,
  channelActionsLog,
  channelHeaderButtonsComponent,
  channelServerBlock,
  confirmToPublish,
  creationDate,
  currentChannelMetadata,
  draft,
  editingTranslation,
  editorFieldsConfig,
  form,
  formErrors,
  formFields,
  hasRedirection,
  hasScheduledPublication,
  id,
  isDraft,
  isPublished,
  versionIsPublished,
  isVideoTranscoded,
  mandatoryFieldsErrors,
  master,
  masterLanguage,
  metadata,
  modifiedChannels,
  preloadPreview,
  previewUrl,
  publicationDate,
  publicUrl,
  publishOnVideoTranscoded,
  redirectMetadata,
  requiredForPublishErrors,
  scheduleDate,
  scheduleUnpublishDate,
  title,
  type,
  unboundFields,
  unpublishedDate,
  versionable,
  hasBeenPublished,
  entityPublishedVersionStartPublicationDate,
  advancedMode,
};
